import React, { useState, useEffect } from 'react';
import { Droplets, Coins, Zap, Home, RefreshCw, Bot, Waves, ShoppingCart, Wrench, Trophy, Map, ChevronDown, AlertCircle, CheckCircle } from 'lucide-react';

// API Base URL
const API_URL = process.env.REACT_APP_API_URL || 'https://ahantravyuh.com/wari/api';

// Types
interface RobotType {
  id: string;
  name: string;
  description: string;
  ahant_cost: number;
  max_durability: number;
  cleaning_power: number;
  repair_urja_cost: number;
  specialization: string | null;
  icon: string;
  rarity: string;
}

interface UserRobot {
  id: string;
  name: string;
  type_name: string;
  icon: string;
  rarity: string;
  current_durability: number;
  max_durability: number;
  durability_percent: number;
  cleaning_power: number;
  repair_urja_cost: number;
  specialization: string | null;
  total_cleanups: number;
  total_wari_earned: number;
  can_clean: boolean;
}

interface Pollutant {
  name: string;
  icon: string;
  severity: number;
  concentration: number;
}

interface WaterBody {
  id: string;
  name: string;
  type: 'ocean' | 'sea' | 'river' | 'lake';
  region: string;
  country: string;
  description: string;
  pollution_level: number;
  initial_pollution: number;
  cleanup_progress: number;
  pollutants: Pollutant[];
}

interface UserStats {
  rank: string;
  total_cleanups: number;
  total_wari_earned: number;
  current_wari_balance: number;
  total_pollution_cleaned: number;
  total_robots_owned: number;
  total_robots_purchased: number;
  total_ahant_spent: number;
  total_urja_spent: number;
}

interface LeaderboardEntry {
  position: number;
  username: string;
  rank: string;
  total_cleanups: number;
  total_wari_earned: number;
}

type TabType = 'water' | 'robots' | 'shop' | 'stats';

function App() {
  const [ahantBalance, setAhantBalance] = useState<number>(0);
  const [urjaBalance, setUrjaBalance] = useState<number>(0);
  const [wariBalance, setWariBalance] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');

  const [activeTab, setActiveTab] = useState<TabType>('water');
  const [robotTypes, setRobotTypes] = useState<RobotType[]>([]);
  const [userRobots, setUserRobots] = useState<UserRobot[]>([]);
  const [waterBodies, setWaterBodies] = useState<WaterBody[]>([]);
  const [waterFilter, setWaterFilter] = useState<string>('all');
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);

  const [selectedRobot, setSelectedRobot] = useState<UserRobot | null>(null);
  const [selectedWaterBody, setSelectedWaterBody] = useState<WaterBody | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      fetchAllData();
    } else {
      setLoading(false);
    }
  }, [isLoggedIn]);

  const showMessage = (msg: string, type: 'success' | 'error') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 4000);
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchBalance(),
      fetchRobotTypes(),
      fetchUserRobots(),
      fetchWaterBodies(),
      fetchStats(),
      fetchLeaderboard()
    ]);
    setLoading(false);
  };

  const fetchBalance = async () => {
    try {
      const res = await fetch(`${API_URL}/balance`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setAhantBalance(data.ahant_balance);
        setUrjaBalance(data.urja_balance);
        setWariBalance(data.wari_balance || 0);
      }
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  };

  const fetchRobotTypes = async () => {
    try {
      const res = await fetch(`${API_URL}/robot-types`);
      const data = await res.json();
      if (data.success) {
        setRobotTypes(data.robot_types);
      }
    } catch (error) {
      console.error('Error fetching robot types:', error);
    }
  };

  const fetchUserRobots = async () => {
    try {
      const res = await fetch(`${API_URL}/my-robots`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setUserRobots(data.robots);
      }
    } catch (error) {
      console.error('Error fetching user robots:', error);
    }
  };

  const fetchWaterBodies = async () => {
    try {
      const url = waterFilter === 'all' ? `${API_URL}/water-bodies` : `${API_URL}/water-bodies?type=${waterFilter}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.success) {
        setWaterBodies(data.water_bodies);
      }
    } catch (error) {
      console.error('Error fetching water bodies:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/my-stats`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setUserStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await fetch(`${API_URL}/leaderboard`);
      const data = await res.json();
      if (data.success) {
        setLeaderboard(data.leaderboard);
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchWaterBodies();
    }
  }, [waterFilter]);

  const buyRobot = async (robotTypeId: string) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/buy-robot`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ robot_type_id: robotTypeId })
      });
      const data = await res.json();
      if (data.success) {
        showMessage(data.message, 'success');
        await Promise.all([fetchBalance(), fetchUserRobots(), fetchStats()]);
      } else {
        showMessage(data.message || 'Failed to buy robot', 'error');
      }
    } catch (error) {
      showMessage('Error buying robot', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const repairRobot = async (robotId: string) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/repair-robot`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ robot_id: robotId })
      });
      const data = await res.json();
      if (data.success) {
        showMessage(data.message, 'success');
        await Promise.all([fetchBalance(), fetchUserRobots(), fetchStats()]);
      } else {
        showMessage(data.message || 'Failed to repair robot', 'error');
      }
    } catch (error) {
      showMessage('Error repairing robot', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const performCleanup = async () => {
    if (!selectedRobot || !selectedWaterBody) {
      showMessage('Select a robot and water body', 'error');
      return;
    }
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/cleanup`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ robot_id: selectedRobot.id, water_body_id: selectedWaterBody.id })
      });
      const data = await res.json();
      if (data.success) {
        showMessage(`+${data.cleanup_details.wari_earned} WARI! Cleaned ${data.cleanup_details.pollutant_icon} ${data.cleanup_details.pollutant_cleaned}`, 'success');
        await Promise.all([fetchBalance(), fetchUserRobots(), fetchWaterBodies(), fetchStats()]);
        // Update selected robot state
        const updatedRobots = userRobots.map(r =>
          r.id === selectedRobot.id
            ? { ...r, current_durability: data.robot_status.durability, can_clean: !data.robot_status.needs_repair }
            : r
        );
        const updatedRobot = updatedRobots.find(r => r.id === selectedRobot.id);
        if (updatedRobot) setSelectedRobot(updatedRobot);
      } else {
        showMessage(data.message || 'Cleanup failed', 'error');
      }
    } catch (error) {
      showMessage('Error during cleanup', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return 'border-slate-500 bg-slate-800';
      case 'uncommon': return 'border-green-500 bg-green-900/20';
      case 'rare': return 'border-blue-500 bg-blue-900/20';
      case 'epic': return 'border-purple-500 bg-purple-900/20';
      case 'legendary': return 'border-yellow-500 bg-yellow-900/20';
      default: return 'border-slate-500 bg-slate-800';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'ocean': return '🌊';
      case 'sea': return '🌅';
      case 'river': return '🏞️';
      case 'lake': return '💧';
      default: return '💧';
    }
  };

  const getPollutionColor = (level: number) => {
    if (level >= 80) return 'text-red-400';
    if (level >= 60) return 'text-orange-400';
    if (level >= 40) return 'text-yellow-400';
    if (level >= 20) return 'text-green-400';
    return 'text-cyan-400';
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-4 py-3">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <Droplets className="w-8 h-8 text-cyan-400" />
            <div>
              <h1 className="text-2xl font-bold text-cyan-400">WARI</h1>
              <p className="text-xs text-slate-400">Save Earth's Waters</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isLoggedIn && (
              <>
                <div className="bg-slate-700 px-3 py-2 rounded-lg flex items-center gap-2">
                  <Coins className="w-4 h-4 text-purple-400" />
                  <span className="font-semibold text-sm">{ahantBalance.toFixed(0)} AHANT</span>
                </div>
                <div className="bg-slate-700 px-3 py-2 rounded-lg flex items-center gap-2">
                  <Zap className="w-4 h-4 text-yellow-400" />
                  <span className="font-semibold text-sm">{urjaBalance.toFixed(0)} URJA</span>
                </div>
                <div className="bg-cyan-900/50 border border-cyan-700 px-3 py-2 rounded-lg flex items-center gap-2">
                  <Droplets className="w-4 h-4 text-cyan-400" />
                  <span className="font-semibold text-sm">{wariBalance.toFixed(0)} WARI</span>
                </div>
                <button onClick={fetchAllData} className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg" title="Refresh">
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </>
            )}
            <button onClick={() => window.location.href = '/'} className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 px-3 py-2 rounded-lg text-sm">
              <Home className="w-4 h-4" /> Home
            </button>
          </div>
        </div>
      </header>

      {/* Message Banner */}
      {message && (
        <div className={`px-4 py-3 flex items-center justify-center gap-2 ${messageType === 'success' ? 'bg-green-800' : 'bg-red-800'}`}>
          {messageType === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message}
        </div>
      )}

      {/* Tab Navigation */}
      {isLoggedIn && !loading && (
        <nav className="bg-slate-800/50 border-b border-slate-700">
          <div className="max-w-7xl mx-auto flex">
            {[
              { id: 'water', label: 'Water Bodies', icon: <Map className="w-4 h-4" /> },
              { id: 'robots', label: 'My Robots', icon: <Bot className="w-4 h-4" /> },
              { id: 'shop', label: 'Robot Shop', icon: <ShoppingCart className="w-4 h-4" /> },
              { id: 'stats', label: 'Stats & Leaderboard', icon: <Trophy className="w-4 h-4" /> }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-cyan-400 text-cyan-400 bg-slate-800'
                    : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>
        </nav>
      )}

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Login Required Notice */}
        {!isLoggedIn && !loading && (
          <div className="text-center py-12">
            <Waves className="w-24 h-24 text-cyan-500 mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">Clean Earth's Water Bodies</h2>
            <p className="text-slate-400 mb-8 max-w-xl mx-auto">
              Deploy cleanup robots to remove pollution from oceans, seas, rivers, and lakes.
              Earn WARI tokens for your contribution.
            </p>
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-8 max-w-md mx-auto">
              <h3 className="text-xl font-semibold text-cyan-400 mb-4">Login Required</h3>
              <p className="text-slate-400 mb-6">Please login via the main AhantraVyuh app to play WARI.</p>
              <button onClick={() => window.location.href = '/'} className="bg-cyan-600 hover:bg-cyan-700 px-6 py-3 rounded-lg font-semibold w-full">
                Go to Main App
              </button>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <RefreshCw className="w-12 h-12 text-cyan-400 animate-spin mx-auto mb-4" />
            <p className="text-slate-400">Loading game data...</p>
          </div>
        )}

        {/* Water Bodies Tab */}
        {isLoggedIn && !loading && activeTab === 'water' && (
          <div>
            {/* Selected Robot & Water Body Panel */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 mb-6">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm text-slate-400 mb-1 block">Selected Robot:</label>
                  {selectedRobot ? (
                    <div className={`${getRarityColor(selectedRobot.rarity)} border rounded-lg p-3 flex items-center gap-3`}>
                      <span className="text-2xl">{selectedRobot.icon}</span>
                      <div>
                        <p className="font-semibold">{selectedRobot.name}</p>
                        <p className="text-xs text-slate-400">
                          Power: {selectedRobot.cleaning_power}x | Durability: {selectedRobot.current_durability}/{selectedRobot.max_durability}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-slate-700 border border-slate-600 rounded-lg p-3 text-slate-400 text-sm">
                      No robot selected - Go to My Robots tab
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm text-slate-400 mb-1 block">Selected Water Body:</label>
                  {selectedWaterBody ? (
                    <div className="bg-slate-700 border border-cyan-700 rounded-lg p-3">
                      <p className="font-semibold">{getTypeIcon(selectedWaterBody.type)} {selectedWaterBody.name}</p>
                      <p className="text-xs text-slate-400">
                        Pollution: <span className={getPollutionColor(selectedWaterBody.pollution_level)}>{selectedWaterBody.pollution_level.toFixed(1)}%</span>
                      </p>
                    </div>
                  ) : (
                    <div className="bg-slate-700 border border-slate-600 rounded-lg p-3 text-slate-400 text-sm">
                      Click a water body below to select
                    </div>
                  )}
                </div>
                <button
                  onClick={performCleanup}
                  disabled={!selectedRobot || !selectedWaterBody || !selectedRobot.can_clean || actionLoading}
                  className={`px-6 py-4 rounded-xl font-bold text-lg flex items-center gap-2 ${
                    selectedRobot && selectedWaterBody && selectedRobot.can_clean && !actionLoading
                      ? 'bg-cyan-600 hover:bg-cyan-700'
                      : 'bg-slate-600 cursor-not-allowed'
                  }`}
                >
                  {actionLoading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Waves className="w-5 h-5" />}
                  CLEAN
                </button>
              </div>
            </div>

            {/* Filter */}
            <div className="flex items-center gap-4 mb-4">
              <span className="text-slate-400">Filter:</span>
              {['all', 'ocean', 'sea', 'river', 'lake'].map(type => (
                <button
                  key={type}
                  onClick={() => setWaterFilter(type)}
                  className={`px-3 py-1 rounded-lg text-sm capitalize ${
                    waterFilter === type ? 'bg-cyan-600' : 'bg-slate-700 hover:bg-slate-600'
                  }`}
                >
                  {type === 'all' ? 'All' : `${getTypeIcon(type)} ${type}s`}
                </button>
              ))}
            </div>

            {/* Water Bodies Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {waterBodies.map(wb => (
                <div
                  key={wb.id}
                  onClick={() => setSelectedWaterBody(wb)}
                  className={`bg-slate-800 border rounded-xl p-4 cursor-pointer transition-all hover:scale-[1.02] ${
                    selectedWaterBody?.id === wb.id ? 'border-cyan-400 ring-2 ring-cyan-400/50' : 'border-slate-700'
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <span className="text-2xl mr-2">{getTypeIcon(wb.type)}</span>
                      <span className="font-semibold">{wb.name}</span>
                    </div>
                    <span className={`text-lg font-bold ${getPollutionColor(wb.pollution_level)}`}>
                      {wb.pollution_level.toFixed(0)}%
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mb-3">{wb.region} • {wb.country}</p>

                  {/* Pollution Bar */}
                  <div className="h-2 bg-slate-700 rounded-full mb-3 overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        wb.pollution_level >= 80 ? 'bg-red-500' :
                        wb.pollution_level >= 60 ? 'bg-orange-500' :
                        wb.pollution_level >= 40 ? 'bg-yellow-500' :
                        wb.pollution_level >= 20 ? 'bg-green-500' : 'bg-cyan-500'
                      }`}
                      style={{ width: `${wb.pollution_level}%` }}
                    />
                  </div>

                  {/* Pollutants */}
                  <div className="flex flex-wrap gap-1">
                    {wb.pollutants.slice(0, 3).map((p, i) => (
                      <span key={i} className="bg-slate-700 px-2 py-1 rounded text-xs" title={p.name}>
                        {p.icon}
                      </span>
                    ))}
                    {wb.pollutants.length > 3 && (
                      <span className="bg-slate-700 px-2 py-1 rounded text-xs">+{wb.pollutants.length - 3}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* My Robots Tab */}
        {isLoggedIn && !loading && activeTab === 'robots' && (
          <div>
            {userRobots.length === 0 ? (
              <div className="text-center py-12 bg-slate-800 rounded-xl border border-slate-700">
                <Bot className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Robots Yet</h3>
                <p className="text-slate-400 mb-4">Visit the Robot Shop to purchase your first cleanup robot!</p>
                <button onClick={() => setActiveTab('shop')} className="bg-cyan-600 hover:bg-cyan-700 px-6 py-3 rounded-lg">
                  Go to Shop
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {userRobots.map(robot => (
                  <div key={robot.id} className={`${getRarityColor(robot.rarity)} border-2 rounded-xl p-4`}>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-4xl">{robot.icon}</span>
                        <div>
                          <p className="font-bold">{robot.name}</p>
                          <p className="text-sm text-slate-400">{robot.type_name}</p>
                          <span className={`text-xs px-2 py-0.5 rounded capitalize ${
                            robot.rarity === 'legendary' ? 'bg-yellow-600' :
                            robot.rarity === 'epic' ? 'bg-purple-600' :
                            robot.rarity === 'rare' ? 'bg-blue-600' :
                            robot.rarity === 'uncommon' ? 'bg-green-600' : 'bg-slate-600'
                          }`}>
                            {robot.rarity}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                      <div className="bg-slate-800/50 rounded p-2">
                        <p className="text-slate-400 text-xs">Power</p>
                        <p className="font-semibold">{robot.cleaning_power}x</p>
                      </div>
                      <div className="bg-slate-800/50 rounded p-2">
                        <p className="text-slate-400 text-xs">Cleanups</p>
                        <p className="font-semibold">{robot.total_cleanups}</p>
                      </div>
                      <div className="bg-slate-800/50 rounded p-2">
                        <p className="text-slate-400 text-xs">WARI Earned</p>
                        <p className="font-semibold text-cyan-400">{robot.total_wari_earned.toFixed(0)}</p>
                      </div>
                      <div className="bg-slate-800/50 rounded p-2">
                        <p className="text-slate-400 text-xs">Specialization</p>
                        <p className="font-semibold text-xs">{robot.specialization || 'None'}</p>
                      </div>
                    </div>

                    {/* Durability Bar */}
                    <div className="mb-3">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-400">Durability</span>
                        <span className={robot.can_clean ? 'text-green-400' : 'text-red-400'}>
                          {robot.current_durability}/{robot.max_durability}
                        </span>
                      </div>
                      <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all ${
                            robot.durability_percent > 50 ? 'bg-green-500' :
                            robot.durability_percent > 25 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${robot.durability_percent}%` }}
                        />
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedRobot(robot)}
                        disabled={!robot.can_clean}
                        className={`flex-1 py-2 rounded-lg flex items-center justify-center gap-2 ${
                          robot.can_clean
                            ? selectedRobot?.id === robot.id
                              ? 'bg-cyan-600'
                              : 'bg-cyan-700 hover:bg-cyan-600'
                            : 'bg-slate-600 cursor-not-allowed'
                        }`}
                      >
                        <Waves className="w-4 h-4" />
                        {selectedRobot?.id === robot.id ? 'Selected' : 'Select'}
                      </button>
                      <button
                        onClick={() => repairRobot(robot.id)}
                        disabled={robot.current_durability >= robot.max_durability || actionLoading}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                          robot.current_durability < robot.max_durability && !actionLoading
                            ? 'bg-yellow-600 hover:bg-yellow-500'
                            : 'bg-slate-600 cursor-not-allowed'
                        }`}
                        title={`Repair for ${robot.repair_urja_cost} URJA`}
                      >
                        <Wrench className="w-4 h-4" />
                        {robot.repair_urja_cost}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Robot Shop Tab */}
        {isLoggedIn && !loading && activeTab === 'shop' && (
          <div>
            <p className="text-slate-400 mb-6">Purchase cleanup robots with AHANT to clean water bodies and earn WARI tokens.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {robotTypes.map(robot => (
                <div key={robot.id} className={`${getRarityColor(robot.rarity)} border-2 rounded-xl p-4`}>
                  <div className="text-center mb-4">
                    <span className="text-5xl">{robot.icon}</span>
                    <h3 className="font-bold text-lg mt-2">{robot.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded capitalize ${
                      robot.rarity === 'legendary' ? 'bg-yellow-600' :
                      robot.rarity === 'epic' ? 'bg-purple-600' :
                      robot.rarity === 'rare' ? 'bg-blue-600' :
                      robot.rarity === 'uncommon' ? 'bg-green-600' : 'bg-slate-600'
                    }`}>
                      {robot.rarity}
                    </span>
                  </div>

                  <p className="text-sm text-slate-400 mb-4 min-h-[40px]">{robot.description}</p>

                  <div className="grid grid-cols-2 gap-2 text-sm mb-4">
                    <div className="bg-slate-800/50 rounded p-2 text-center">
                      <p className="text-slate-400 text-xs">Power</p>
                      <p className="font-semibold">{robot.cleaning_power}x</p>
                    </div>
                    <div className="bg-slate-800/50 rounded p-2 text-center">
                      <p className="text-slate-400 text-xs">Durability</p>
                      <p className="font-semibold">{robot.max_durability}</p>
                    </div>
                    <div className="bg-slate-800/50 rounded p-2 text-center">
                      <p className="text-slate-400 text-xs">Repair Cost</p>
                      <p className="font-semibold text-yellow-400">{robot.repair_urja_cost} URJA</p>
                    </div>
                    <div className="bg-slate-800/50 rounded p-2 text-center">
                      <p className="text-slate-400 text-xs">Specialization</p>
                      <p className="font-semibold text-xs">{robot.specialization || 'All'}</p>
                    </div>
                  </div>

                  <button
                    onClick={() => buyRobot(robot.id)}
                    disabled={ahantBalance < robot.ahant_cost || actionLoading}
                    className={`w-full py-3 rounded-lg font-bold flex items-center justify-center gap-2 ${
                      ahantBalance >= robot.ahant_cost && !actionLoading
                        ? 'bg-purple-600 hover:bg-purple-500'
                        : 'bg-slate-600 cursor-not-allowed'
                    }`}
                  >
                    <Coins className="w-5 h-5" />
                    {robot.ahant_cost} AHANT
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats & Leaderboard Tab */}
        {isLoggedIn && !loading && activeTab === 'stats' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* User Stats */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-xl font-bold text-cyan-400 mb-4 flex items-center gap-2">
                <Trophy className="w-6 h-6" /> Your Stats
              </h3>
              {userStats && (
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 rounded-xl p-4 text-center">
                    <p className="text-slate-400 text-sm">Current Rank</p>
                    <p className="text-2xl font-bold text-cyan-400">{userStats.rank}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-700/50 rounded-lg p-3">
                      <p className="text-slate-400 text-xs">Total Cleanups</p>
                      <p className="text-xl font-bold">{userStats.total_cleanups}</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-3">
                      <p className="text-slate-400 text-xs">WARI Earned</p>
                      <p className="text-xl font-bold text-cyan-400">{userStats.total_wari_earned.toFixed(0)}</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-3">
                      <p className="text-slate-400 text-xs">Pollution Cleaned</p>
                      <p className="text-xl font-bold text-green-400">{userStats.total_pollution_cleaned.toFixed(1)}</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-3">
                      <p className="text-slate-400 text-xs">Robots Owned</p>
                      <p className="text-xl font-bold">{userStats.total_robots_owned}</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-3">
                      <p className="text-slate-400 text-xs">AHANT Spent</p>
                      <p className="text-xl font-bold text-purple-400">{userStats.total_ahant_spent.toFixed(0)}</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-3">
                      <p className="text-slate-400 text-xs">URJA Spent</p>
                      <p className="text-xl font-bold text-yellow-400">{userStats.total_urja_spent.toFixed(0)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Leaderboard */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-xl font-bold text-cyan-400 mb-4 flex items-center gap-2">
                <Trophy className="w-6 h-6" /> Leaderboard
              </h3>
              {leaderboard.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No entries yet. Be the first!</p>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {leaderboard.map((entry, i) => (
                    <div key={i} className={`flex items-center gap-3 p-3 rounded-lg ${
                      i === 0 ? 'bg-yellow-900/30 border border-yellow-700' :
                      i === 1 ? 'bg-slate-600/30 border border-slate-500' :
                      i === 2 ? 'bg-orange-900/30 border border-orange-700' :
                      'bg-slate-700/30'
                    }`}>
                      <span className={`text-2xl font-bold w-8 text-center ${
                        i === 0 ? 'text-yellow-400' :
                        i === 1 ? 'text-slate-300' :
                        i === 2 ? 'text-orange-400' : 'text-slate-500'
                      }`}>
                        {entry.position}
                      </span>
                      <div className="flex-1">
                        <p className="font-semibold">{entry.username}</p>
                        <p className="text-xs text-slate-400">{entry.rank}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-cyan-400">{entry.total_wari_earned.toFixed(0)} WARI</p>
                        <p className="text-xs text-slate-400">{entry.total_cleanups} cleanups</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-10 border-t border-slate-800 px-6 py-6 text-center text-slate-500 text-sm">
        <p>WARI - Part of Ahantravyuh Platform</p>
        <p className="mt-1">Save Earth's Waters - Clean Our Oceans, Rivers & Lakes</p>
      </footer>
    </div>
  );
}

export default App;
